/**
 * FreeTierDashboard — upload-first layout for free accounts.
 * Shows a 30-day countdown once a report has been submitted; until then the
 * upload zone is prominent and the only call-to-action.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { UploadZone } from '../components/UploadZone.jsx';
import { RecentReportsTable } from '../components/RecentReportsTable.jsx';
import { UpgradeCTA } from '../components/UpgradeCTA.jsx';
import { formatDateDDMonYYYY } from '../../utils/dates.js';

export function FreeTierDashboard({ user, organization, navigate }) {
  const [usage, setUsage] = useState(null);
  const [recent, setRecent] = useState([]);
  const [recentError, setRecentError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([api.getOrgUsage(), api.getRecentReports()])
      .then(([uRes, rRes]) => {
        if (cancelled) return;
        setUsage(uRes.status === 'fulfilled' ? uRes.value : null);
        if (rRes.status === 'fulfilled') {
          setRecent(Array.isArray(rRes.value?.reports) ? rRes.value.reports : []);
          setRecentError(null);
        } else {
          setRecent([]);
          setRecentError(rRes.reason);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const isAdmin = user?.role === 'admin';
  const subStatus = organization?.subscription_status;
  const isReadOnlyAdmin =
    isAdmin && (subStatus === 'expired' || subStatus === 'read_only');
  const blocked = usage?.free_next_report_at && usage?.free_days_remaining > 0;
  // SURFACE-COHERE (item E): the wall's first line states the exact date the
  // next free report unlocks — computed from the SAME field the `blocked` gate
  // reads (usage.free_next_report_at, ISO from /usage ← enforceFreeTierLimit.js)
  // and formatted with the shared "DD Mmm YYYY" helper the PDF cover uses.
  const nextDateLabel = useMemo(() => {
    if (!blocked) return null;
    return formatDateDDMonYYYY(usage.free_next_report_at);
  }, [blocked, usage]);
  // Plan description derived from the live cap numbers (single source of truth =
  // /usage ← enforceFreeTierLimit.js); no hardcoded "one report / 30 days".
  const planLabel = useMemo(() => {
    const limit = usage?.free_report_limit;
    const days = usage?.free_window_days;
    if (!limit || !days) return null;
    const reports = `${limit} schedule health ${limit === 1 ? 'report' : 'reports'}`;
    return `${reports} every ${days} ${days === 1 ? 'day' : 'days'}`;
  }, [usage]);

  return (
    <main className="main-content dashboard-free" role="main">
      <section className="dashboard-hero">
        <h1>Welcome{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}.</h1>
        <p>
          You&rsquo;re on the <strong>Free</strong> plan{planLabel ? ` — ${planLabel}` : ''}.
        </p>
      </section>

      {blocked ? (
        <section className="free-countdown-card" aria-live="polite">
          <h2>Next free report available on {nextDateLabel}.</h2>
          <p>
            Unlock unlimited uploads, project history, and team access with a paid plan.
          </p>
          <UpgradeCTA label="Upgrade to Starter" navigate={navigate} />
        </section>
      ) : (
        <UploadZone
          onUploaded={(d) => navigate(`/reports/${d.report_id}`)}
          disabled={isReadOnlyAdmin}
          disabledReason={isReadOnlyAdmin ? 'Read-only mode. Renew to upload schedules.' : null}
        />
      )}

      <section className="recent-reports">
        <h2>Your report history</h2>
        {recentError && (
          <p className="recent-reports-error" role="alert">
            Couldn&rsquo;t load recent reports.
          </p>
        )}
        <RecentReportsTable reports={recent} navigate={navigate} />
      </section>
    </main>
  );
}
